import { useState } from 'react';

export default function Legal() {
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms' | 'cookies'>('privacy');

  const legalContent = {
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'January 2025',
      sections: [
        {
          heading: 'Information We Collect',
          content: 'ALAMAR S.R.L. is committed to protecting your privacy. We collect minimal information necessary to provide our VPN service, including account credentials and payment information. We do not log your browsing activity, connection timestamps, IP addresses, or DNS queries.'
        },
        {
          heading: 'How We Use Your Information',
          content: 'The information we collect is used solely to maintain your account, process payments, and provide customer support. We never sell, rent, or share your personal information with third parties for marketing purposes.'
        },
        {
          heading: 'Data Security',
          content: 'We implement industry-standard security measures to protect your data, including AES-256 encryption, secure servers, and regular security audits. Your data is stored securely and accessed only by authorized personnel.'
        },
        {
          heading: 'Your Rights',
          content: 'You have the right to access, modify, or delete your personal information at any time. You can also request a copy of your data or withdraw consent for data processing by contacting our support team.'
        }
      ]
    },
    terms: {
      title: 'Terms of Service',
      lastUpdated: 'January 2025',
      sections: [
        {
          heading: 'Acceptance of Terms',
          content: 'By using ALAMAR VPN services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.'
        },
        {
          heading: 'Service Description',
          content: 'ALAMAR provides VPN services that encrypt your internet connection and protect your online privacy. We strive to maintain high service availability but do not guarantee uninterrupted service.'
        },
        {
          heading: 'Acceptable Use',
          content: 'You agree to use our service only for lawful purposes. Prohibited activities include but are not limited to: illegal file sharing, hacking, spamming, or any activity that violates local, national, or international laws.'
        },
        {
          heading: 'Account Responsibilities',
          content: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use.'
        },
        {
          heading: 'Limitation of Liability',
          content: 'ALAMAR S.R.L. shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our service.'
        }
      ]
    },
    cookies: {
      title: 'Cookie Policy',
      lastUpdated: 'January 2025',
      sections: [
        {
          heading: 'What Are Cookies',
          content: 'Cookies are small text files stored on your device when you visit our website. They help us provide a better user experience and analyze website traffic.'
        },
        {
          heading: 'Types of Cookies We Use',
          content: 'We use essential cookies for website functionality, analytics cookies to understand how visitors use our site, and preference cookies to remember your settings. We do not use advertising or tracking cookies.'
        },
        {
          heading: 'Managing Cookies',
          content: 'You can control and delete cookies through your browser settings. However, disabling cookies may affect the functionality of our website and your ability to use certain features.'
        },
        {
          heading: 'Third-Party Cookies',
          content: 'We may use third-party services that set their own cookies. These services are carefully selected and comply with privacy regulations.'
        }
      ]
    }
  };

  const currentContent = legalContent[activeTab];

  return (
    <section id="legal" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">
            LEGAL INFORMATION
          </span>
          <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
            Transparency &amp; Trust
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            We believe in complete transparency. Read our policies to understand how we protect your privacy and data.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('privacy')}
              className={`flex-1 px-6 py-4 font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'privacy'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-shield-check-line mr-2"></i>
              Privacy Policy
            </button>
            <button
              onClick={() => setActiveTab('terms')}
              className={`flex-1 px-6 py-4 font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'terms'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-file-text-line mr-2"></i>
              Terms of Service
            </button>
            <button
              onClick={() => setActiveTab('cookies')}
              className={`flex-1 px-6 py-4 font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'cookies'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-cookie-line mr-2"></i>
              Cookie Policy
            </button>
          </div>

          <div className="p-8 md:p-12">
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                {currentContent.title}
              </h3>
              <p className="text-gray-500 text-sm">
                Last updated: {currentContent.lastUpdated}
              </p>
            </div>

            <div className="space-y-8">
              {currentContent.sections.map((section, index) => (
                <div key={index} className="border-l-4 border-purple-600 pl-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">
                    {section.heading}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-xl text-white"></i>
                </div>
                <div>
                  <h5 className="font-bold text-gray-900 mb-2">Questions about our policies?</h5>
                  <p className="text-gray-600 text-sm mb-3">
                    If you have any questions or concerns about our legal policies, please don't hesitate to contact us.
                  </p>
                  <a
                    href="mailto:support@a-lamar-srl.com"
                    className="text-blue-600 font-semibold hover:text-blue-700 transition-colors text-sm cursor-pointer inline-flex items-center whitespace-nowrap"
                  >
                    Contact Support
                    <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
